/**
 * reveal-on-scroll.js
 *
 * Plays the shared .slow-reveal arrival (assets/base.css) when a block is
 * scrolled to, rather than on page load. Used by the Intention page, whose
 * four beats run a long way down the page: revealed on load they would
 * finish long before anyone scrolled far enough to see them.
 *
 * Mark a block with data-reveal-on-scroll. Its direct children then arrive
 * in sequence, the same as any .slow-reveal block.
 *
 * Two rules keep this from ever hiding content it shouldn't:
 *
 * 1. Only blocks that start below the fold are held back. A block already
 *    on screen when this runs is left exactly as it is. This script is
 *    deferred, so the page has usually painted by now — hiding something
 *    the visitor can already see, to then fade it in, would read as a
 *    flicker rather than as an arrival.
 * 2. Nothing is hidden by the stylesheet alone. The holding class is only
 *    ever added here, so a failed download, a thrown error or scripting
 *    switched off all leave the page fully readable with no animation.
 *    Same principle as the intro failsafe in snippets/motion-head.liquid:
 *    the worst case is a visitor who misses the animation.
 */
(function () {
  'use strict';

  function reveal(el) {
    el.classList.remove('is-reveal-pending');
    /* Adding the class is what starts the animation: the rules in
       base.css only match once .slow-reveal is present, and a CSS
       animation begins when its name first applies to the element. */
    el.classList.add('slow-reveal');
  }

  function init() {
    var blocks = document.querySelectorAll('[data-reveal-on-scroll]');
    if (!blocks.length) return;

    var reduced = false;
    try {
      reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {}

    /* Asked for less motion: leave every block alone, already visible. */
    if (reduced) return;

    var pending = [];
    Array.prototype.forEach.call(blocks, function (el) {
      if (el.getBoundingClientRect().top <= (window.innerHeight || 0)) return;
      el.classList.add('is-reveal-pending');
      pending.push(el);
    });
    if (!pending.length) return;

    /* A scroll check rather than an IntersectionObserver, for one reason:
       an observer only reports a block it actually caught inside the
       viewport. Jump past one — an anchor link, a restored scroll
       position, a fast flick on a phone — and it is never seen
       intersecting, so it would stay hidden for good. Re-reading positions
       means anything now at or above the fold reveals, whether it was
       scrolled to or skipped over.

       The check runs straight off the scroll event rather than being
       deferred into a requestAnimationFrame. Browsers already coalesce
       scroll events to one a frame, reading a handful of positions is
       nothing, and rAF is suspended entirely in a document that is not
       being rendered — which would leave a page opened in a background
       tab with its copy held back until something else woke it. */
    function check() {
      var trigger = (window.innerHeight || 0) * 0.85;
      for (var i = pending.length - 1; i >= 0; i--) {
        if (pending[i].getBoundingClientRect().top > trigger) continue;
        reveal(pending[i]);
        pending.splice(i, 1);
      }
      if (!pending.length) {
        window.removeEventListener('scroll', check);
        window.removeEventListener('resize', check);
      }
    }

    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    check();
  }

  /* On load, not DOMContentLoaded. A deferred script runs as soon as the
     HTML is parsed, which can be before the stylesheets have applied — and
     measuring "is this block below the fold" against an unstyled page gets
     the wrong answer for every one of them. Unstyled, the full-bleed image
     on the Intention page renders at its natural 2400px and pushes the
     whole document down, so blocks that actually sit inside the first
     viewport measure as being far below it, get held back, and then sit
     blank on screen.

     By load, the stylesheets and the images with reserved dimensions are
     all in, so the measurement is against the layout the visitor is
     actually looking at. Holding something back this late is invisible
     precisely because it is off screen. */
  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }
})();
